import type { Content, Equipment } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/** Le nom d'une jauge, pour lire un effet sans deviner. */
function nomDeJauge(content: Content, gaugeId: string): string {
  return content.jauges.find((j) => j.id === gaugeId)?.nom ?? gaugeId;
}

/** Les effets d'un équipement, écrits en clair. */
function Effets({
  content,
  effets,
}: {
  content: Content;
  effets: Record<string, number | undefined>;
}) {
  const lignes = Object.entries(effets).filter(
    ([, valeur]) => valeur !== undefined && valeur !== 0,
  );
  if (lignes.length === 0) {
    return null;
  }

  return (
    <ul className="mt-1 space-y-1">
      {lignes.map(([gaugeId, valeur]) => (
        <li key={gaugeId} className="text-slate-800">
          {nomDeJauge(content, gaugeId)} :{' '}
          <span className="font-semibold tabular-nums">
            {(valeur as number) > 0 ? `+${valeur}` : valeur}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * La fiche détaillée d'un équipement : ce qu'il mesure, ce qu'il coûte, ce qu'il
 * change. Elle s'ouvre au clic sur le ⓘ, donc sans survol — une tablette n'en a pas.
 */
export function FicheEquipement({
  content,
  equipement,
  onFermer,
}: {
  content: Content;
  equipement: Equipment;
  onFermer: () => void;
}) {
  const caseDeChaine = content.chapitres
    .find((c) => c.id === 'ch2')
    ?.cases_chaine?.find((c) => c.id === equipement.role);

  const usages = equipement.usages
    .map((problemId) => content.problemes.find((p) => p.id === problemId)?.titre)
    .filter((titre): titre is string => titre !== undefined);

  return (
    <section
      aria-label={equipement.nom}
      className="rounded-2xl border-2 border-sky-300 bg-white p-4 shadow-sm"
    >
      <header className="flex items-start gap-3">
        <span aria-hidden="true" className="text-3xl leading-none">
          {equipement.icone}
        </span>
        <h3 className="min-w-0 flex-1 text-lg font-bold text-slate-900">{equipement.nom}</h3>
        <button
          type="button"
          onClick={onFermer}
          aria-label={T.concevoir.fermerFiche}
          className="min-h-touch min-w-touch rounded-lg text-slate-500 hover:bg-slate-100"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </header>

      <dl className="mt-3 space-y-3">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {T.concevoir.role}
          </dt>
          <dd className="text-slate-800">{caseDeChaine?.nom ?? equipement.role}</dd>
        </div>

        {equipement.mesure && (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {T.concevoir.mesure}
            </dt>
            <dd className="text-slate-800">{equipement.mesure}</dd>
          </div>
        )}

        {equipement.description && (
          <div>
            <dd className="text-slate-800">{equipement.description}</dd>
          </div>
        )}

        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {T.concevoir.cout}
          </dt>
          <dd className="text-lg font-bold tabular-nums text-slate-900">
            {equipement.cout} {content.meta.unite_budget}
          </dd>
        </div>

        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {T.concevoir.impactFabrication}
          </dt>
          <dd>
            <Effets content={content} effets={equipement.impact_fabrication} />
          </dd>
        </div>

        {equipement.effet_permanent && (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {T.concevoir.effetPermanent}
            </dt>
            <dd>
              <Effets content={content} effets={equipement.effet_permanent} />
            </dd>
          </div>
        )}

        {usages.length > 0 && (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {T.concevoir.usages}
            </dt>
            <dd>
              <ul className="list-inside list-disc text-slate-800">
                {usages.map((titre) => (
                  <li key={titre}>{titre}</li>
                ))}
              </ul>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}
